import CertificateChainWarningModal from "components/molecules/CertificateChainWarningModal";
import ModalWithButtons from "components/molecules/Modal/ModalWithButtons";
import { useCallback, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { translations } from "translations";
import { certicateUploadProcessTypeAtom } from "../state/uploadCertificateStateBase";
import { CerticateUploadProcessType } from "../types";
import useCertificateReplacement from "../hooks/useCertificateReplacement";
import useCertificateReplacementGridForming from "pages/UploadGridFormingCertificate/hooks/useCertificateReplacement";
import ReplaceCertificateSelect from "./ReplaceCertificateSelect";
import SelectProcess from "./SelectProcess";
import SelectGridFormingType from "./SelectGridFormingType";
import { useGetCertificateChainInfoQuery } from "graphqlBase/certificateChainInfo/__generated__/getCertificateChainInfo";
import { CertificateFormType } from "graphqlBase/types";
import { getGridFormingCertificateUploadPath } from "./navigation";

const CerticateUploadProcesSelection: React.FC = () => {
  const navigate = useNavigate();
  const [certificateId, setCertificateId] = useState<string | undefined>(undefined);
  const [selectedCertificateFormType, setSelectedCertificateFormType] = useState<
    CertificateFormType | null | undefined
  >(undefined);
  const [processOption, setProcessOption] = useState<number>(0);
  const [gridFormingType, setGridFormingType] = useState<number>(0);
  const [alignment, setAlignment] = useState<"withdraw" | "extend">("withdraw");
  const [certicateUploadProcess, setCerticateUploadProcess] = useRecoilState(certicateUploadProcessTypeAtom);
  const { setReplacement, resetReplacement } = useCertificateReplacement();
  const { setReplacement: setReplacementGridForming, resetReplacement: resetReplacementGridForming } =
    useCertificateReplacementGridForming();
  const [showChainWarning, setShowChainWarning] = useState(false);

  // Use the GraphQL query to check if certificate is part of a chain
  const { data: chainInfoData, loading: chainInfoLoading } = useGetCertificateChainInfoQuery({
    variables: { certificateId: certificateId || "" },
    skip: !certificateId, // Skip query if no certificate ID
    fetchPolicy: "cache-and-network",
  });

  // Determine if certificate is part of a chain
  const isCertificatePartOfChain = useMemo(() => {
    if (!chainInfoData?.certificateChainInfo || chainInfoLoading) return false;

    const chainInfo = chainInfoData.certificateChainInfo;

    // Check if there are multiple certificates in the chain OR if this certificate has chainLevel > 0
    return chainInfo.length > 1 || chainInfo.some((cert) => cert.chainLevel > 0);
  }, [chainInfoData, chainInfoLoading]);

  // Get sorted chain certificates for display in modal
  const chainCertificates = useMemo(() => {
    if (!chainInfoData?.certificateChainInfo) return [];

    // Sort by chain level descending (oldest/base certificate first, then extensions)
    return chainInfoData.certificateChainInfo
      .map((cert) => ({
        certificateId: cert.certificateId,
        certificateNumber: cert.certificateNumber,
        certificateCode: cert.certificateCode,
        chainLevel: cert.chainLevel,
      }))
      .sort((a, b) => b.chainLevel - a.chainLevel);
  }, [chainInfoData]);

  const goToCertificateUpload = useCallback(() => {
    navigate("/certificate-upload");
  }, [navigate]);

  const goToGridFormingCertificateUpload = useCallback(
    (matrixType?: number) => {
      navigate(getGridFormingCertificateUploadPath(matrixType));
    },
    [navigate]
  );

  const onClose = useCallback(() => {
    setCerticateUploadProcess(undefined);
    setProcessOption(0);
    setGridFormingType(0);
  }, [setCerticateUploadProcess]);

  const onBackFromGridFormingType = useCallback(() => {
    setCerticateUploadProcess(CerticateUploadProcessType.toDecide);
    setGridFormingType(0);
  }, [setCerticateUploadProcess]);

  // Modified submit function to check for chain and route based on certificate type
  const submitForNewCertificate = useCallback(() => {
    if (certificateId) {
      // Only check for chain in replacement flow (withdraw)
      if (alignment === "withdraw" && isCertificatePartOfChain) {
        setShowChainWarning(true);
        return;
      }

      const replacementData = {
        certificateId,
        operation: alignment === "extend" ? ("extend" as const) : ("replace" as const),
      };

      // Set replacement state in both standard and grid forming atoms
      // This ensures that regardless of which flow the user navigates to,
      // the replacement state is available
      setReplacement(replacementData);
      setReplacementGridForming(replacementData);
    }
    setCerticateUploadProcess(undefined);
    setCertificateId(undefined);
    onClose();

    // Route based on certificate form type
    // Don't pass matrixType for replace/extend - it's detected from loaded certificate data
    if (selectedCertificateFormType === "GRID_FORMING") {
      goToGridFormingCertificateUpload();
    } else {
      goToCertificateUpload();
    }
  }, [
    certificateId,
    setCerticateUploadProcess,
    onClose,
    goToCertificateUpload,
    goToGridFormingCertificateUpload,
    alignment,
    setReplacement,
    setReplacementGridForming,
    isCertificatePartOfChain,
    selectedCertificateFormType,
  ]);

  // Handler for confirming the warning modal
  const handleConfirmChainWarning = () => {
    setShowChainWarning(false);
    if (certificateId) {
      setReplacement({
        certificateId,
        operation: alignment === "extend" ? "extend" : "replace",
      });
    }
    setCerticateUploadProcess(undefined);
    setCertificateId(undefined);
    onClose();

    // Route based on certificate form type
    // Don't pass matrixType for replace/extend - it's detected from loaded certificate data
    if (selectedCertificateFormType === "GRID_FORMING") {
      goToGridFormingCertificateUpload();
    } else {
      goToCertificateUpload();
    }
  };

  // Handler for certificate selection with type information
  const handleCertificateSelect = useCallback((certificateFormType?: string | null | undefined) => {
    setSelectedCertificateFormType(certificateFormType as CertificateFormType);
  }, []);

  // Handler for cancelling the warning modal
  const handleCancelChainWarning = () => {
    setShowChainWarning(false);
  };

  const continueDisabled =
    (certicateUploadProcess === CerticateUploadProcessType.toDecide && processOption === 0) ||
    (certicateUploadProcess === CerticateUploadProcessType.replaceCertificate && (!certificateId || !alignment)) ||
    (certicateUploadProcess === CerticateUploadProcessType.selectGridFormingType && gridFormingType === 0);

  const onContinue = useCallback(() => {
    if (continueDisabled) return;
    if (CerticateUploadProcessType.replaceCertificate === certicateUploadProcess) {
      submitForNewCertificate();
    }
    if (CerticateUploadProcessType.toDecide === certicateUploadProcess) {
      if (processOption === 1) {
        resetReplacement();
        resetReplacementGridForming();
        onClose();
        goToCertificateUpload();
      } else if (processOption === 2) {
        setCerticateUploadProcess(CerticateUploadProcessType.replaceCertificate);
      } else if (processOption === 3) {
        setCerticateUploadProcess(CerticateUploadProcessType.selectGridFormingType);
      }
    }
    if (CerticateUploadProcessType.selectGridFormingType === certicateUploadProcess) {
      resetReplacement();
      resetReplacementGridForming();
      onClose();
      goToGridFormingCertificateUpload(gridFormingType);
    }
  }, [
    certicateUploadProcess,
    continueDisabled,
    goToCertificateUpload,
    goToGridFormingCertificateUpload,
    gridFormingType,
    onClose,
    processOption,
    resetReplacement,
    resetReplacementGridForming,
    setCerticateUploadProcess,
    submitForNewCertificate,
  ]);

  return (
    <>
      <ModalWithButtons
        title={
          certicateUploadProcess === CerticateUploadProcessType.toDecide
            ? translations.pages.certificateUpload.label
            : certicateUploadProcess === CerticateUploadProcessType.selectGridFormingType
            ? translations.pages.certificateUpload.gridFormingTypeSelection.title
            : translations.pages.certificateUpload.certificateUploadProcess.replaceExisting.title
        }
        openModal={!!certicateUploadProcess}
        onClose={onClose}
        cancelClick={
          certicateUploadProcess === CerticateUploadProcessType.selectGridFormingType
            ? onBackFromGridFormingType
            : onClose
        }
        addButtonTitle={translations.globals.button.continue}
        cancelTitle={translations.globals.button.back}
        addButtonClick={onContinue}
        width="auto"
        minHeight={certicateUploadProcess === CerticateUploadProcessType.replaceCertificate ? "650px" : undefined}
        maxHeight={certicateUploadProcess === CerticateUploadProcessType.replaceCertificate ? "120vh" : undefined}
        disabled={continueDisabled}
      >
        {certicateUploadProcess === CerticateUploadProcessType.toDecide && (
          <SelectProcess setProcessOption={setProcessOption} processOption={processOption} />
        )}
        {certicateUploadProcess === CerticateUploadProcessType.selectGridFormingType && (
          <SelectGridFormingType setGridFormingType={setGridFormingType} gridFormingType={gridFormingType} />
        )}
        {certicateUploadProcess === CerticateUploadProcessType.replaceCertificate && (
          <ReplaceCertificateSelect
            alignment={alignment}
            setAlignment={setAlignment}
            setCertificateId={setCertificateId}
            onCertificateSelect={handleCertificateSelect}
          />
        )}
      </ModalWithButtons>
      {/* Warning Modal for Extension Chain */}
      <CertificateChainWarningModal
        isOpen={showChainWarning}
        onClose={handleCancelChainWarning}
        onConfirm={handleConfirmChainWarning}
        chainCertificates={chainCertificates}
        currentCertificateId={certificateId || ""}
        context="replace"
      />
    </>
  );
};
export default CerticateUploadProcesSelection;
